/**
 * TKT-2026-107051. What a trade-in line of several copies is worth, walked on the
 * store's quantity ladder exactly as the buylist books it.
 *
 * A store that runs reduction thresholds pays less for each copy past a stock
 * level: holding 3 with "3:10% 4:20%", the 4th copy is offered at -10% and the 5th
 * at -20%. The engine's single-unit offer (`${cond}FinalCash`) is the price of the
 * NEXT copy only. The kiosk used to multiply that one figure by the quantity, so a
 * customer selling 5 copies was shown, and the till was handed, the first copy's
 * price for all 5.
 *
 * THE WALK IS NOT HERE. It is sortswift-buylist-api's own walk (the one that books
 * the order, src/utils/buylistTieredLine.js), vendored byte for byte into
 * ./vendor/buylistTierWalk.js by that repo's scripts/vendor-buylist-walk.js. This
 * file used to carry a hand port of it, and the ports drifted: the standalone
 * storefront priced copies past the last tier at 0 while the server did not.
 * Never edit the vendored file; vendor/buylistTierWalk.test.js fails on any edit and
 * names the command that regenerates it.
 *
 * What is left here only adapts a kiosk cart line to that walk: the ladder is the
 * engine's quote kept on the line as `buylistLadder`, the quantity is `qty`, the
 * condition is `variant.condAbbr`, and the per-copy offer is `buylistCashPrice` /
 * `buylistCreditPrice`. The server's fold (applyTieredUnitOffers) decides the
 * per-copy figure, so per-copy x quantity equals the ladder total here exactly as it
 * does in the order.
 */
import {
    applyTieredUnitOffers,
    calculateTieredLineTotal,
    cartCountOffsets,
    hasTierData,
    resolveTierCondPrefix as resolveLinePrefix,
    roundOffer,
} from './vendor/buylistTierWalk';

export { hasTierData, roundOffer };

/**
 * The part of an engine price answer the walk needs, kept on the cart line.
 * Null when the store runs no quantity ladder for this card.
 */
export function ladderFromQuote(quote) {
    if (!hasTierData(quote)) return null;
    return {
        basePrices: quote.basePrices,
        tieredPricing: quote.tieredPricing,
        offerRounding: quote.offerRounding || null,
        inventoryQuantity: quote.inventoryQuantity || 0,
    };
}

/**
 * The price-key prefix a line is filed under. Sealed product is condition 'U',
 * which the engine files under NM, so any condition the base prices lack reads NM.
 */
export function resolveTierCondPrefix(condAbbr, ladder) {
    return resolveLinePrefix({ condAbbr }, ladder);
}

/** Walk `quantity` copies across the ladder and return the line total. */
export function ladderLineTotal(ladder, priceKey, quantity, currency = null) {
    return calculateTieredLineTotal({ quantity }, priceKey, currency, ladder);
}

/** The ladder a cart line walks on: a buy line's engine quote, or null. */
const buyLadderOf = (item) => ((item?.mode || 'sell') === 'buy' && hasTierData(item?.buylistLadder) ? item.buylistLadder : null);

/**
 * For each cart line, the copies of the same card the buy lines BEFORE it carry
 * (TKT-2026-108650). A store that counts every printing of a card together has the
 * engine tag each ladder with a count key; two printings of one card in the cart
 * walk one count, as the order books them. Every other cart gets all zeros.
 */
export function buyLineCountOffsets(cart) {
    const lines = (cart || []).map((item) => ({ quantity: item?.qty, ladder: buyLadderOf(item) }));
    return cartCountOffsets(lines, (line) => line.ladder);
}

/**
 * The per-copy cash and credit offer for a buy-mode cart line at its quantity.
 * A line with no ladder keeps the single-unit offer it was added with.
 * `alreadyInCart` is the line's buyLineCountOffsets entry.
 */
export function buyLineUnitOffers(item, currency = null, alreadyInCart = 0) {
    const line = {
        quantity: item?.qty,
        condAbbr: item?.variant?.condAbbr,
        addedCashPrice: Number(item?.buylistCashPrice) || 0,
        addedCreditPrice: Number(item?.buylistCreditPrice) || 0,
    };
    applyTieredUnitOffers([line], currency, () => item?.buylistLadder || null, [alreadyInCart]);
    return { cash: line.addedCashPrice, credit: line.addedCreditPrice };
}

/**
 * The cart as every screen, total and payload reads it: each buy line's
 * buylistCashPrice / buylistCreditPrice is its per-copy offer at its quantity.
 * The stored line keeps the single-unit offer and the ladder, so changing the
 * quantity re-walks from the engine's figures rather than from a derived one.
 */
export function foldBuyLadders(cart, currency = null) {
    const offsets = buyLineCountOffsets(cart);
    return cart.map((item, index) => {
        if (!buyLadderOf(item)) return item;
        const { cash, credit } = buyLineUnitOffers(item, currency, offsets[index]);
        return { ...item, buylistCashPrice: cash, buylistCreditPrice: credit };
    });
}
